import { useEffect, useState } from "react";
import useSWR, { useSWRConfig } from "swr";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useNavigate, useLocation, Link } from "react-router";
import { useSelector, useDispatch } from "react-redux";
import { fetcherSimple } from "utils/api";
import { API_SERVER, STRIPE_PUBLISHABLE_KEY } from "utils/config";
import { userSelector } from "store/user";
import { metaSelector, setSubscriptionDialogOpen } from "store/meta";
import { getValidUserLimit, resolvePlanLabel } from "utils/consts";
import ContentWrapper from "components/ui/content-wrapper";
import {
    Card,
    CardHeader,
    CardBody,
    CardFooter,
    Button,
    Typography,
    Avatar,
} from "@material-tailwind/react";
import MaterialIcon from "components/material-icon";
import Pagination from "components/pagination";
import profilepic from "assets/img/uploadprofilepic.png";
import {
    GobbyTable,
    GobbyTableHead,
    GobbyTableBody,
    GobbyTableRow,
    GobbyTableHeader,
    GobbyTableCell,
} from "components/ui/gobby-table";

export default function Dashboard() {
    const { mutate } = useSWRConfig();
    const user = useSelector(userSelector);
    const meta = useSelector(metaSelector);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    dayjs.extend(relativeTime);
    const [timestamp] = useState(dayjs());
    const [activeListPage, setActiveListPage] = useState(1);
    const [draftListPage, setDraftListPage] = useState(1);
    const [renewDate, setRenewDate] = useState("");
    const [renewWhen, setRenewWhen] = useState("");
    const [period, setPeriod] = useState("");

    // Dashboard intentionally overrides the global `revalidateOnFocus: false`
    // (set in app.js) for credits and purchases so billing data is fresh on
    // tab return. See architecture report F34. If this becomes a pattern,
    // extract a `useFreshSWR` hook rather than spreading more overrides.
    const { data: creditsData } = useSWR(
        [`/v2/organisation/credits`, "GET"],
        ([path, method]) => fetcherSimple(path, method),
        {
            revalidateOnFocus: true,
            revalidateOnReconnect: true,
        }
    );

    const { data: purchasesData } = useSWR(
        [`/v2/organisation/purchases`, "GET"],
        ([path, method]) => fetcherSimple(path, method),
        {
            revalidateOnFocus: true,
            revalidateOnReconnect: true,
        }
    );

    const { data: userData } = useSWR(
        [`/v2/user/@me`, "GET"],
        ([path, method]) => fetcherSimple(path, method),
    );

    const { data: orgUsers } = useSWR(
        [`/v2/organisation/users?status=active`, "GET"],
        ([path, method]) => fetcherSimple(path, method),
    );

    const { data: orgData } = useSWR(
        [`/v2/organisation`, "GET"],
        ([path, method]) => fetcherSimple(path, method),
    );

    const { data: activeSurveyStats } = useSWR(
        [`/v2/survey?state=active&stats=1&page=${activeListPage}`, "GET"],
        ([path, method]) => fetcherSimple(path, method),
    );

    const { data: draftSurveyStats } = useSWR(
        [`/v2/survey?state=draft&stats=1&page=${draftListPage}`, "GET"],
        ([path, method]) => fetcherSimple(path, method),
    );

    const { data: overallStats } = useSWR(
        [`/v2/survey?stats=2`, "GET"],
        ([path, method]) => fetcherSimple(path, method),
    );

    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const paymentSuccess = urlParams.get('payment_success');

        if (paymentSuccess === 'true') {
            mutate([`/v2/organisation/purchases`, "GET"]);
            navigate(location.pathname, { replace: true });
        }
    }, [mutate, navigate, location.pathname, location.search]);

    useEffect(() => {
        if (purchasesData?.length > 0) {
            const recurring_purchase = purchasesData.find(
                (product) => product?.expires,
            );
            const expiry_date = recurring_purchase?.expires;
            if (expiry_date && dayjs(expiry_date).isValid()) {
                const renewDateDayjs = dayjs(expiry_date);
                const renewDateString =
                    dayjs(renewDateDayjs).format("DD/MM/YYYY");
                const renewWhenString = dayjs().to(renewDateDayjs, true);
                setRenewDate(renewDateString);
                setRenewWhen(renewWhenString);
                setPeriod(recurring_purchase?.renews_after || 'year');
            }
        }
    }, [purchasesData]);

    const pfpImageUrl =
        API_SERVER + `/v2/user/@me/picture`;

    return (
        <>
            <ContentWrapper>
                <div className="mb-10 text-start rounded-xl lg:p-6 p-4">
                    <div className="lg:flex lg:flex-row lg:gap-4">
                        <div className="flex-1 min-h-[100px] pr-2">
                            {/* Account Status moved to top as per issue #918 */}
                            <div className="mb-4">
                                <Card className="card min-h-[100px] rounded-2xl py-4 px-4">
                                    <CardHeader
                                        floated={false}
                                        shadow={false}
                                        className="card-inner-header w-full m-0 text-lg font-display pb-2"
                                    >
                                        <Typography variant="h4" className="card-header-text">
                                            Account Status
                                        </Typography>
                                    </CardHeader>
                                    <CardBody className="card-inner-body overflow-x-auto">
                                        <div>
                                            <GobbyTable>
                                                <GobbyTableHead>
                                                    <GobbyTableRow>
                                                        <GobbyTableHeader>Account Status</GobbyTableHeader>
                                                        <GobbyTableHeader>Subscription Type</GobbyTableHeader>
                                                        <GobbyTableHeader>Credits Available</GobbyTableHeader>
                                                        <GobbyTableHeader>Renewal Date</GobbyTableHeader>
                                                        <GobbyTableHeader>Seats Occupied</GobbyTableHeader>
                                                    </GobbyTableRow>
                                                </GobbyTableHead>
                                                <GobbyTableBody>
                                                    {userData && creditsData && orgUsers ? (
                                                        <>
                                                            <GobbyTableRow>
                                                                <GobbyTableCell>
                                                                    {userData?.active ? "Active" : "Inactive"}
                                                                </GobbyTableCell>
                                                                <GobbyTableCell>
                                                                    {resolvePlanLabel(user?.isManaged, user?.subscriptionDetails, orgData?.subscription_name)}
                                                                </GobbyTableCell>
                                                                <GobbyTableCell>
                                                                    {creditsData?.one_time_credits_remaining || 0} (One-time) + {creditsData?.recurring_credits_remaining || 0} (Recurring)
                                                                </GobbyTableCell>
                                                                <GobbyTableCell>
                                                                    {renewDate || "N/A"}
                                                                </GobbyTableCell>
                                                                <GobbyTableCell>
                                                                    {`${orgUsers?.length || 0} / ${getValidUserLimit(orgData?.user_limit)}`}
                                                                </GobbyTableCell>
                                                            </GobbyTableRow>
                                                            <GobbyTableRow>
                                                                <GobbyTableCell className="font-semibold">Member ID</GobbyTableCell>
                                                                <GobbyTableCell colSpan="4">
                                                                    {userData?.id || "N/A"}
                                                                </GobbyTableCell>
                                                            </GobbyTableRow>
                                                            <GobbyTableRow>
                                                                <GobbyTableCell className="font-semibold">Organisation</GobbyTableCell>
                                                                <GobbyTableCell colSpan="4">
                                                                    {userData?.organisation || "N/A"}
                                                                </GobbyTableCell>
                                                            </GobbyTableRow>
                                                            <GobbyTableRow>
                                                                <GobbyTableCell className="font-semibold">Plan Length</GobbyTableCell>
                                                                <GobbyTableCell colSpan="4">
                                                                    {period || "N/A"}
                                                                </GobbyTableCell>
                                                            </GobbyTableRow>
                                                        </>
                                                    ) : (
                                                        <GobbyTableRow>
                                                            <GobbyTableCell colSpan="5">
                                                                Loading account status...
                                                            </GobbyTableCell>
                                                        </GobbyTableRow>
                                                    )}
                                                </GobbyTableBody>
                                            </GobbyTable>
                                        </div>
                                    </CardBody>
                                </Card>
                            </div>

                            <div className="mt-4">
                                <Card className="card min-h-[100px] rounded-2xl py-4 px-4">
                                    <CardHeader
                                        floated={false}
                                        shadow={false}
                                        className="card-inner-header w-full m-0 text-lg font-display pb-2"
                                    >
                                        <Typography variant="h4" className="card-header-text">
                                            Active Surveys
                                        </Typography>
                                    </CardHeader>
                                    <CardBody className="card-inner-body overflow-x-auto">
                                        <div>
                                            <GobbyTable>
                                                <GobbyTableHead>
                                                    <GobbyTableRow>
                                                        <GobbyTableHeader>
                                                            <div className="flex items-end">
                                                                <MaterialIcon name="summarize" />
                                                                    &nbsp;Survey Name
                                                            </div>
                                                        </GobbyTableHeader>
                                                        <GobbyTableHeader>
                                                            <div className="flex items-end">
                                                                <MaterialIcon name="group" />
                                                                    &nbsp;Total Visitors
                                                            </div>
                                                        </GobbyTableHeader>
                                                        <GobbyTableHeader>
                                                            <div className="flex items-end">
                                                                <MaterialIcon name="report" />
                                                                    &nbsp;Moderation
                                                                    Requests
                                                            </div>
                                                        </GobbyTableHeader>
                                                        <GobbyTableHeader>
                                                            <div className="flex items-end">
                                                                <MaterialIcon name="insights" />
                                                                    &nbsp;Themes
                                                            </div>
                                                        </GobbyTableHeader>
                                                        <GobbyTableHeader>
                                                            <div className="flex items-end">
                                                                <MaterialIcon name="calendar_month" />
                                                                    &nbsp;Closing Date
                                                            </div>
                                                        </GobbyTableHeader>
                                                    </GobbyTableRow>
                                                </GobbyTableHead>
                                                <GobbyTableBody>
                                                    {activeSurveyStats &&
                                                        activeSurveyStats.data &&
                                                        activeSurveyStats.data.map(
                                                            (survey) => (
                                                                <GobbyTableRow key={survey.nanoid}>
                                                                    <GobbyTableCell className="px-2 py-1 text-sm">
                                                                        <Link
                                                                            to={`/survey/${survey.id}`}
                                                                            state={{
                                                                                defaultTab:
                                                                                    "reports",
                                                                            }}
                                                                            className="hover:text-blue-500"
                                                                        >
                                                                            {
                                                                                survey.name
                                                                            }
                                                                        </Link>
                                                                    </GobbyTableCell>
                                                                    <GobbyTableCell className="px-2 py-1 text-sm">
                                                                        {
                                                                            survey
                                                                                .respondents
                                                                                .total
                                                                        }
                                                                    </GobbyTableCell>
                                                                    <GobbyTableCell className="px-2 py-1 text-sm">
                                                                        <Link
                                                                            to={`/survey/${survey.id}`}
                                                                            state={{
                                                                                defaultTab:
                                                                                    "moderation",
                                                                            }}
                                                                            className="hover:text-blue-500"
                                                                        >
                                                                            {
                                                                                survey
                                                                                    .abuse_reports
                                                                                    .pending
                                                                            }
                                                                        </Link>
                                                                    </GobbyTableCell>
                                                                    <GobbyTableCell className="px-2 py-1 text-sm">
                                                                        {
                                                                            survey.themes
                                                                        }
                                                                    </GobbyTableCell>
                                                                    <GobbyTableCell className="px-2 py-1 text-sm">
                                                                        {dayjs(
                                                                            survey.end_date,
                                                                            "YYYY-MM-DD HH:mm:ss",
                                                                        ).format(
                                                                            "DD/MM/YYYY",
                                                                        )}
                                                                    </GobbyTableCell>
                                                                </GobbyTableRow>
                                                            ),
                                                        )}
                                                </GobbyTableBody>
                                            </GobbyTable>
                                        </div>
                                        {activeSurveyStats &&
                                            activeSurveyStats.pager && (
                                            <Pagination
                                                currentPage={activeSurveyStats.pager.current_page}
                                                lastPage={activeSurveyStats.pager.last_page}
                                                onPageChange={setActiveListPage}
                                            />
                                        )}
                                    </CardBody>
                                </Card>
                            </div>
                            <div className="mt-4">
                                <Card className="card min-h-[100px] rounded-2xl py-4 px-4">
                                    <CardHeader
                                        floated={false}
                                        shadow={false}
                                        className="card-inner-header w-full m-0 text-lg font-display pb-2"
                                    >
                                        <Typography variant="h4" className="card-header-text">
                                            Draft Surveys
                                        </Typography>
                                    </CardHeader>
                                    <CardBody className="card-inner-body">
                                        <GobbyTable>
                                            <GobbyTableHead>
                                                <GobbyTableRow>
                                                    <GobbyTableHeader>
                                                        <div className="flex items-end">
                                                            <MaterialIcon name="summarize" />
                                                                &nbsp;Survey Name
                                                        </div>
                                                    </GobbyTableHeader>
                                                    <GobbyTableHeader>
                                                        <div className="flex items-end">
                                                            <MaterialIcon name="insights" />
                                                                &nbsp;Themes
                                                        </div>
                                                    </GobbyTableHeader>
                                                    <GobbyTableHeader>
                                                        <div className="flex items-end">
                                                            <MaterialIcon name="calendar_month" />
                                                                &nbsp;Date Created
                                                        </div>
                                                    </GobbyTableHeader>
                                                </GobbyTableRow>
                                            </GobbyTableHead>
                                            <GobbyTableBody>
                                                {draftSurveyStats &&
                                                        draftSurveyStats.data &&
                                                        draftSurveyStats.data.map(
                                                            (survey) => (
                                                                <GobbyTableRow key={survey.nanoid}>
                                                                    <GobbyTableCell>
                                                                        {
                                                                            survey.name
                                                                        }
                                                                    </GobbyTableCell>
                                                                    <GobbyTableCell>
                                                                        {
                                                                            survey
                                                                                .abuse_reports
                                                                                .pending
                                                                        }
                                                                    </GobbyTableCell>
                                                                    <GobbyTableCell>
                                                                        {dayjs(
                                                                            survey.create_date,
                                                                            "YYYY-MM-DD HH:mm:ss",
                                                                        ).format(
                                                                            "DD/MM/YYYY",
                                                                        )}
                                                                    </GobbyTableCell>
                                                                </GobbyTableRow>
                                                            ),
                                                        )}
                                            </GobbyTableBody>
                                        </GobbyTable>
                                        {draftSurveyStats &&
                                            draftSurveyStats.pager && (
                                            <Pagination
                                                currentPage={draftSurveyStats.pager.current_page}
                                                lastPage={draftSurveyStats.pager.last_page}
                                                onPageChange={setDraftListPage}
                                            />
                                        )}
                                    </CardBody>
                                </Card>
                            </div>

                            {/* Commented out top boxes as per issue #918 */}
                            {/*
                            <div className="grid sm:grid-cols-2 gap-4 grid-cols-1">
                                <Card className="card min-h-[100px] rounded-2xl py-4 px-4">
                                    <CardHeader
                                        floated={false}
                                        shadow={false}
                                        className="card-inner-header w-full m-0 text-lg font-display pb-2"
                                    >
                                        <Typography variant="h4" className="card-header-text">
                                            General Info
                                        </Typography>
                                    </CardHeader>
                                    <CardBody className="card-inner-body">
                                        <GobbyTable>
                                            <GobbyTableBody>
                                                <GobbyTableRow>
                                                    <Tooltip
                                                        content="All surveys (draft, finished, open, paused) created for this account, does not include removed surveys."
                                                        animate={{
                                                            mount: {
                                                                scale: 1,
                                                                y: 0,
                                                            },
                                                            unmount: {
                                                                scale: 0,
                                                                y: 25,
                                                            },
                                                        }}
                                                    >
                                                        <GobbyTableCell className="cursor-help">
                                                            Total Surveys
                                                        </GobbyTableCell>
                                                    </Tooltip>
                                                    <GobbyTableCell className="text-sm">
                                                        {overallStats?.stats
                                                            .total_surveys ||
                                                            "0"}
                                                    </GobbyTableCell>
                                                </GobbyTableRow>
                                                <GobbyTableRow>
                                                    <Tooltip
                                                        content="Number of respondents across all surveys"
                                                        animate={{
                                                            mount: {
                                                                scale: 1,
                                                                y: 0,
                                                            },
                                                            unmount: {
                                                                scale: 0,
                                                                y: 25,
                                                            },
                                                        }}
                                                    >
                                                        <GobbyTableCell className="cursor-help">
                                                            Total Visitors
                                                        </GobbyTableCell>
                                                    </Tooltip>
                                                    <GobbyTableCell>
                                                        {overallStats?.stats
                                                            .total_respondents ||
                                                            "0"}
                                                    </GobbyTableCell>
                                                </GobbyTableRow>
                                                <GobbyTableRow>
                                                    <Tooltip
                                                        content="Number of surveys currently live (does not include paused surveys)"
                                                        animate={{
                                                            mount: {
                                                                scale: 1,
                                                                y: 0,
                                                            },
                                                            unmount: {
                                                                scale: 0,
                                                                y: 25,
                                                            },
                                                        }}
                                                    >
                                                        <GobbyTableCell className="cursor-help">
                                                            Surveys Live
                                                        </GobbyTableCell>
                                                    </Tooltip>
                                                    <GobbyTableCell>
                                                        {overallStats?.stats
                                                            .surveys_live ||
                                                            "0"}
                                                    </GobbyTableCell>
                                                </GobbyTableRow>
                                                <GobbyTableRow>
                                                    <Tooltip
                                                        content="Number of respondents active on live surveys"
                                                        animate={{
                                                            mount: {
                                                                scale: 1,
                                                                y: 0,
                                                            },
                                                            unmount: {
                                                                scale: 0,
                                                                y: 25,
                                                            },
                                                        }}
                                                    >
                                                        <GobbyTableCell className="cursor-help">
                                                            Respondents Online
                                                        </GobbyTableCell>
                                                    </Tooltip>
                                                    <GobbyTableCell>
                                                        {overallStats?.stats
                                                            .respondents_online ||
                                                            "0"}
                                                    </GobbyTableCell>
                                                </GobbyTableRow>
                                                <GobbyTableRow>
                                                    <Tooltip
                                                        content="Moderation responses awaiting an admin to select status"
                                                        animate={{
                                                            mount: {
                                                                scale: 1,
                                                                y: 0,
                                                            },
                                                            unmount: {
                                                                scale: 0,
                                                                y: 25,
                                                            },
                                                        }}
                                                    >
                                                        <GobbyTableCell className="cursor-help">
                                                            Open Moderations
                                                            (all surveys)
                                                        </GobbyTableCell>
                                                    </Tooltip>
                                                    <GobbyTableCell>
                                                        {overallStats?.stats
                                                            .open_moderations ||
                                                            "0"}
                                                    </GobbyTableCell>
                                                </GobbyTableRow>
                                                <GobbyTableRow>
                                                    <Tooltip
                                                        content="Does Not Expire"
                                                        animate={{
                                                            mount: {
                                                                scale: 1,
                                                                y: 0,
                                                            },
                                                            unmount: {
                                                                scale: 0,
                                                                y: 25,
                                                            },
                                                        }}
                                                    >
                                                        <GobbyTableCell className="cursor-help">
                                                            Paid Credits
                                                            Remaining
                                                        </GobbyTableCell>
                                                    </Tooltip>
                                                    <GobbyTableCell>
                                                        {creditsData?.one_time_credits_remaining ||
                                                            "0"}
                                                    </GobbyTableCell>
                                                </GobbyTableRow>
                                                <GobbyTableRow>
                                                    <Tooltip
                                                        content={
                                                            renewDate &&
                                                            renewWhen
                                                                ? `will renew in ${renewWhen} at ${renewDate}`
                                                                : "No renewal date set"
                                                        }
                                                        animate={{
                                                            mount: {
                                                                scale: 1,
                                                                y: 0,
                                                            },
                                                            unmount: {
                                                                scale: 0,
                                                                y: 25,
                                                            },
                                                        }}
                                                    >
                                                        <GobbyTableCell className="cursor-help">
                                                            Recurring Credits
                                                            Remaining
                                                        </GobbyTableCell>
                                                    </Tooltip>
                                                    <GobbyTableCell>
                                                        {creditsData?.recurring_credits_remaining ||
                                                            "0"}
                                                    </GobbyTableCell>
                                                </GobbyTableRow>
                                            </GobbyTableBody>
                                        </GobbyTable>
                                    </CardBody>
                                </Card>
                                <Card className="card rounded-2xl py-4 px-4">
                                    <CardHeader
                                        floated={false}
                                        shadow={false}
                                        className="card-inner-header w-full m-0 text-lg font-display pb-2"
                                    >
                                        <Typography variant="h4" className="card-header-text">
                                            Surveys by Status
                                        </Typography>
                                    </CardHeader>
                                    <CardBody className="card-inner-body overflow-x-auto">
                                        <div>
                                            <GobbyTable>
                                                <GobbyTableHead>
                                                    <GobbyTableRow>
                                                        <GobbyTableHeader>
                                                            <div className="flex items-end">
                                                                <MaterialIcon name="summarize" />
                                                                    &nbsp;Survey Type
                                                            </div>
                                                        </GobbyTableHeader>
                                                        <GobbyTableHeader>
                                                            <div className="flex items-end">
                                                                <MaterialIcon name="group" />
                                                                    &nbsp;Respondents
                                                            </div>
                                                        </GobbyTableHeader>
                                                    </GobbyTableRow>
                                                </GobbyTableHead>
                                                <GobbyTableBody>
                                                    {(overallStats?.surveys || []).map(
                                                        (survey_state) => (
                                                            <GobbyTableRow key={survey_state.state}>
                                                                <GobbyTableCell className="capitalize">
                                                                    {survey_state.state ||
                                                                        "N/A"}
                                                                </GobbyTableCell>
                                                                <GobbyTableCell>
                                                                    {survey_state.respondent_count ||
                                                                        "0"}
                                                                </GobbyTableCell>
                                                            </GobbyTableRow>
                                                        ),
                                                    )}
                                                </GobbyTableBody>
                                            </GobbyTable>
                                        </div>
                                    </CardBody>
                                </Card>
                            </div>
                            */}
                        </div>
                        <div className="w-full lg:w-1/4 flex-shrink-0 pt-4 lg:pt-0">
                            <Card className="card rounded-2xl p-4">
                                <CardHeader
                                    floated={false}
                                    shadow={false}
                                    className="w-[150px] h-[150px] mx-auto rounded-full"
                                >
                                    <Avatar
                                        variant="circular"
                                        alt="Profile Picture"
                                        className="border border-gray-900 p-0.5 w-full h-full"
                                        src={pfpImageUrl}
                                        onError={({ currentTarget }) => {
                                            currentTarget.onerror = null;
                                            currentTarget.src = `${profilepic}`;
                                        }}
                                    />
                                </CardHeader>
                                <CardBody className="card-inner-body bg-white rounded-t-xl mt-2 p-0 pt-2 mt-4">
                                    <Typography variant="h5" className="pb-4">
                                        {userData?.name || ""}
                                    </Typography>
                                    <div className="flex items-start mb-1">
                                        <MaterialIcon
                                            name="store"
                                            style={{ fontSize: "22px" }}
                                        />
                                        <span className="ml-2">
                                            Org. Name:{" "}
                                            {userData?.organisation || "N/A"}
                                        </span>
                                    </div>
                                    {!!user.userData?.is_main_admin && (
                                        <div className="flex items-start">
                                            <MaterialIcon
                                                name="card_membership"
                                                style={{ fontSize: "22px" }}
                                            />
                                            <span className="ml-2">
                                                {user?.subscriptionDetails || "N/A"}
                                            </span>
                                        </div>
                                    )}
                                    <div className="flex items-start">
                                        <MaterialIcon
                                            name="badge"
                                            style={{ fontSize: "22px" }}
                                        />
                                        <span className="ml-2">
                                            {userData?.job_role || "N/A"}
                                        </span>
                                    </div>
                                </CardBody>
                                <CardFooter className="text-center bg-white rounded-b-xl">
                                    <div className="flex flex-col gap-2">
                                        {!!(user.userData?.is_main_admin && STRIPE_PUBLISHABLE_KEY) && (
                                            <>
                                                {!user?.activeSubscription && !user?.isManaged && (
                                                    <Button
                                                        type="button"
                                                        variant="filled"
                                                        shadow="false"
                                                        size="lg"
                                                        className="btn btn--cta"
                                                        onClick={() => dispatch(setSubscriptionDialogOpen(true))}
                                                    >
                                                        Subscribe
                                                    </Button>
                                                )}
                                                <Button
                                                    type="button"
                                                    variant="filled"
                                                    shadow="false"
                                                    size="lg"
                                                    className="btn btn--primary"
                                                    onClick={() => navigate('/top-ups')}
                                                >
                                                    Top Up
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                </CardFooter>
                            </Card>
                        </div>
                    </div>
                </div>
                <div></div>
            </ContentWrapper>
        </>
    );
}
